import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { getComicById, getRelatedComics } from '@/mocks/comics';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const comic = id ? getComicById(id) : undefined;
  const relatedComics = id ? getRelatedComics(id, 4) : [];

  if (!comic) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-24 pb-14 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-950">漫画未找到</h1>
            <p className="mt-2 text-foreground-500">抱歉，您查找的漫画不存在或已下架</p>
            <Link
              to="/"
              className="whitespace-nowrap inline-flex items-center gap-2 mt-6 px-6 py-3 bg-primary-500 text-white rounded-full font-medium text-sm hover:bg-primary-600 transition-all"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const pageTitle = `${comic.title}_免费${comic.categoryName}韩漫在线观看`;
  const pageDesc = `${comic.title}是韩国漫画网精选${comic.categoryName}韩漫，${comic.description.substring(0, 100)}... 支持韩漫在线观看，免费阅读，高清画质。`;

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${comic.title} - 韩国漫画网`,
    description: comic.description,
    author: {
      '@type': 'Person',
      name: comic.author,
    },
    datePublished: '2026-01-01',
    dateModified: comic.updateDate,
    publisher: {
      '@type': 'Organization',
      name: '韩国漫画网',
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://nextdiscover.cn/' },
      { '@type': 'ListItem', position: 2, name: `${comic.categoryName}韩漫`, item: `https://nextdiscover.cn/category/${comic.category}` },
      { '@type': 'ListItem', position: 3, name: comic.title },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <title>{pageTitle} - 韩国漫画网</title>
      <meta name="description" content={pageDesc} />
      <meta name="keywords" content={`${comic.title},${comic.categoryName}韩漫,免费韩漫,韩漫在线观看,韩国漫画网`} />
      <link rel="canonical" href={`https://nextdiscover.cn/article/${comic.id}`} />
      <meta property="og:title" content={`${pageTitle} - 韩国漫画网`} />
      <meta property="og:description" content={pageDesc} />
      <meta property="og:type" content="article" />
      <meta property="og:url" content={`https://nextdiscover.cn/article/${comic.id}`} />
      <meta property="og:image" content={comic.cover} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={`${pageTitle} - 韩国漫画网`} />
      <meta name="twitter:description" content={pageDesc} />
      <meta name="twitter:image" content={comic.cover} />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>

      <Navbar />
      <main className="pt-24 pb-14">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: `${comic.categoryName}韩漫`, href: `/category/${comic.category}` },
              { label: comic.title },
            ]}
          />

          <article className="mt-8">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-shrink-0">
                <div className="w-48 md:w-64 mx-auto lg:mx-0 rounded-xl overflow-hidden bg-background-200 aspect-[3/4]">
                  <img
                    src={comic.cover}
                    alt={`${comic.title} - 韩国漫画网${comic.categoryName}韩漫`}
                    title={`${comic.title} - 免费韩漫在线观看`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                  {comic.title}
                </h1>

                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <span className="text-sm text-foreground-500">作者：<strong>{comic.author}</strong></span>
                  <span className="text-sm text-foreground-400">|</span>
                  <span className="text-sm text-foreground-500">分类：<strong>{comic.categoryName}</strong></span>
                  <span className="text-sm text-foreground-400">|</span>
                  <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${
                    comic.status === 'completed'
                      ? 'bg-accent-100 text-accent-700'
                      : 'bg-primary-100 text-primary-700'
                  }`}>
                    {comic.status === 'completed' ? '已完结' : '连载中'}
                  </span>
                </div>

                <div className="mt-4 flex flex-wrap items-center gap-2">
                  {comic.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/category/${comic.category}`}
                      className="whitespace-nowrap px-3 py-1 text-xs rounded-full bg-secondary-100 text-secondary-700 hover:bg-secondary-200 transition-colors"
                    >
                      {tag}
                    </Link>
                  ))}
                </div>

                <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="p-4 bg-background-100 rounded-xl border border-background-200/70">
                    <div className="text-xs text-foreground-500">评分</div>
                    <div className="mt-1 text-xl font-heading font-bold text-primary-600">{comic.rating}</div>
                  </div>
                  <div className="p-4 bg-background-100 rounded-xl border border-background-200/70">
                    <div className="text-xs text-foreground-500">章节数</div>
                    <div className="mt-1 text-xl font-heading font-bold text-foreground-950">{comic.chapters}</div>
                  </div>
                  <div className="p-4 bg-background-100 rounded-xl border border-background-200/70">
                    <div className="text-xs text-foreground-500">阅读量</div>
                    <div className="mt-1 text-xl font-heading font-bold text-foreground-950">
                      {comic.views > 10000 ? `${(comic.views / 10000).toFixed(0)}万` : comic.views}
                    </div>
                  </div>
                  <div className="p-4 bg-background-100 rounded-xl border border-background-200/70">
                    <div className="text-xs text-foreground-500">收藏数</div>
                    <div className="mt-1 text-xl font-heading font-bold text-foreground-950">
                      {comic.likes > 10000 ? `${(comic.likes / 10000).toFixed(0)}万` : comic.likes}
                    </div>
                  </div>
                </div>

                <div className="mt-6 flex flex-wrap items-center gap-3">
                  <Link
                    to={`/article/${comic.id}`}
                    className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white rounded-full font-medium text-sm hover:bg-primary-600 transition-all duration-200"
                  >
                    <i className="ri-play-line"></i>
                    开始阅读
                  </Link>
                  <button className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-3 bg-background-100 text-foreground-700 rounded-full font-medium text-sm border border-background-200/70 hover:bg-background-200 transition-all duration-200">
                    <i className="ri-heart-line"></i>
                    收藏
                  </button>
                  <button className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-3 bg-background-100 text-foreground-700 rounded-full font-medium text-sm border border-background-200/70 hover:bg-background-200 transition-all duration-200">
                    <i className="ri-share-forward-line"></i>
                    分享
                  </button>
                </div>
              </div>
            </div>

            <section className="mt-10">
              <h2 className="font-heading text-xl font-bold text-foreground-950">作品简介</h2>
              <div className="mt-4 p-6 bg-background-100 rounded-xl border border-background-200/70">
                <p className="text-sm text-foreground-700 leading-relaxed whitespace-pre-line">
                  {comic.description}
                </p>
              </div>
            </section>

            <section className="mt-10">
              <h2 className="font-heading text-xl font-bold text-foreground-950">章节目录</h2>
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {Array.from({ length: Math.min(comic.chapters, 20) }, (_, i) => i + 1).map((ch) => (
                  <Link
                    key={ch}
                    to={`/article/${comic.id}`}
                    className="block px-4 py-3 bg-background-100 rounded-lg border border-background-200/70 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-all duration-200 text-center whitespace-nowrap"
                  >
                    第 {ch} 话
                  </Link>
                ))}
                {comic.chapters > 20 && (
                  <div className="px-4 py-3 bg-background-100 rounded-lg border border-background-200/70 text-sm text-foreground-400 text-center">
                    更多章节...
                  </div>
                )}
              </div>
            </section>

            {relatedComics.length > 0 && (
              <section className="mt-10">
                <h2 className="font-heading text-xl font-bold text-foreground-950">相关推荐</h2>
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5" data-product-shop="">
                  {relatedComics.map((rel) => (
                    <Link
                      key={rel.id}
                      to={`/article/${rel.id}`}
                      className="group block bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-200"
                    >
                      <div className="relative overflow-hidden aspect-[3/4]">
                        <img
                          src={rel.cover}
                          alt={`${rel.title} - 韩国漫画网推荐`}
                          title={`${rel.title} - 韩漫在线观看推荐`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                        />
                      </div>
                      <div className="p-3">
                        <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                          {rel.title}
                        </h3>
                        <p className="mt-1 text-xs text-foreground-500">{rel.author}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </article>
        </div>
      </main>
      <Footer />
    </>
  );
}